import React, { createContext, useContext, useState, useCallback } from "react";

const AdminUiContext = createContext(null);

/**
 * Provides globally-accessible admin UX helpers:
 *  - openUser(id)     : opens the user profile drawer
 *  - openBooking(id)  : opens the booking detail drawer
 *  - closeDrawers()   : closes both
 *  - onDataChanged(fn): register callbacks that should re-run when a
 *                      drawer mutates data (block/unblock/delete/notes)
 *                      so the underlying admin lists refresh.
 */
export function AdminUiProvider({ children }) {
  const [userId, setUserId] = useState(null);
  const [bookingId, setBookingId] = useState(null);
  const [subscribers] = useState(() => new Set());

  const openUser    = useCallback((id) => setUserId(id), []);
  const openBooking = useCallback((id) => setBookingId(id), []);
  const closeDrawers = useCallback(() => { setUserId(null); setBookingId(null); }, []);
  const notifyChanged = useCallback(() => {
    subscribers.forEach((fn) => { try { fn(); } catch (_) {} });
  }, [subscribers]);
  const onDataChanged = useCallback((fn) => {
    subscribers.add(fn);
    return () => subscribers.delete(fn);
  }, [subscribers]);

  return (
    <AdminUiContext.Provider value={{ userId, bookingId, openUser, openBooking, closeDrawers, onDataChanged, notifyChanged }}>
      {children}
    </AdminUiContext.Provider>
  );
}

export function useAdminUi() {
  const ctx = useContext(AdminUiContext);
  if (!ctx) throw new Error("useAdminUi must be used inside <AdminUiProvider>");
  return ctx;
}
